import React, { useState, useMemo } from "react";
import { Modal, DatePicker, Form } from "antd";
import { useSelector } from "react-redux";
import CustomSelect from "../../../Reuseable/Select/Select";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import "./ExportModal.scss";
import { exportAllocationData } from "../../../assets/api/agency";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import dayjs from "dayjs";
import { useEffect } from "react";

const ExportModal = ({ open, onClose, exportFunction = exportAllocationData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { token, user } = useSelector((state) => state.auth);
  const { filter_list } = useSelector((state) => state.filters);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const portfoliosData = useMemo(() => {
    return (
      filter_list?.find((item) => item?.name === "portfolios")?.filters || []
    );
  }, [filter_list]);

  const dispositionData = useMemo(() => {
    return (
      filter_list?.find((item) => item?.name === "disposition")?.filters || []
    );
  }, [filter_list]);

  const telecallerData = useMemo(() => {
    return (
      filter_list?.find((item) => item?.name === "telecallers")?.filters || []
    );
  }, [filter_list]);

  const portfolioOptions = useMemo(
    () => portfoliosData.map((p) => ({ label: p.name, value: p.id })),
    [portfoliosData],
  );
  const dispositionOptions = useMemo(
    () => dispositionData.map((d) => ({ label: d.value, value: d.key })),
    [dispositionData],
  );
  const telecallerOptions = useMemo(
    () => telecallerData.map((t) => ({ label: t.name, value: t.id })),
    [telecallerData],
  );

  const exactDate = Form.useWatch("date", form);
  const startDate = Form.useWatch("start_date", form);
  const endDate = Form.useWatch("end_date", form);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const params = {};
      if (values.portfolio && values.portfolio.length > 0) {
        params.portfolio = values.portfolio.join(",");
      }
      if (values.disposition && values.disposition.length > 0) {
        params.disposition = values.disposition.join(",");
      }
      if (values.telecaller && values.telecaller.length > 0) {
        params.telecaller = values.telecaller.join(",");
      }
      if (values.date) {
        params.date = dayjs(values.date).format("YYYY-MM-DD");
      }
      if (values.start_date) {
        params.start_date = dayjs(values.start_date).format("YYYY-MM-DD");
      }
      if (values.end_date) {
        params.end_date = dayjs(values.end_date).format("YYYY-MM-DD");
      }

      const res = await exportFunction(params, token);
      console.log(res, "exportDataConsole");
      CustomNotification.show("Export initiated successfully!");
      onClose();
    } catch (error) {
      console.log(error, "error");
      CustomNotification.show(error?.message, 400);
    } finally {
      setLoading(false);
    }
  };

  /* Exact date and range are mutually exclusive - the fields disable each
     other, so the reason is spelled out rather than left to be discovered. */
  const hasRange = Boolean(startDate || endDate);

  return (
    <Modal
      title="Export Cases Data"
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      className="exportModalMain"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="exportForm"
      >
        <section className="exportSection">
          <header className="exportSection__head">
            <span className="exportSection__title">Filters</span>
            <span className="exportSection__hint">
              Leave blank to export everything
            </span>
          </header>

          <div className="RowWrapper">
            <Form.Item name="portfolio" label="Portfolio">
              <CustomSelect
                placeholder="Select Portfolio"
                isMultiSelect={true}
                options={portfolioOptions}
              />
            </Form.Item>
            <Form.Item name="disposition" label="Disposition">
              <CustomSelect
                placeholder="Select Disposition"
                isMultiSelect={true}
                options={dispositionOptions}
              />
            </Form.Item>
          </div>

          {!user?.is_tc && (
            <div className="RowWrapper">
              <Form.Item name="telecaller" label="Telecaller">
                <CustomSelect
                  placeholder="Select Telecaller"
                  isMultiSelect={true}
                  options={telecallerOptions}
                />
              </Form.Item>
            </div>
          )}
        </section>

        <section className="exportSection">
          <header className="exportSection__head">
            <span className="exportSection__title">Period</span>
            <span className="exportSection__hint">
              An exact date or a range, not both
            </span>
          </header>

          <div className="RowWrapper">
            <Form.Item name="date" label="Exact date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                placeholder="Select date"
                disabled={hasRange}
                onChange={(val) => {
                  if (val) {
                    form.setFieldsValue({ start_date: null, end_date: null });
                  }
                }}
              />
            </Form.Item>
          </div>

          <div className="dateRangeWrapper">
            <Form.Item name="start_date" label="Start date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                placeholder="From"
                disabled={!!exactDate}
                onChange={(val) => {
                  if (val) form.setFieldsValue({ date: null });
                }}
              />
            </Form.Item>
            <Form.Item name="end_date" label="End date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                placeholder="To"
                disabled={!!exactDate}
                onChange={(val) => {
                  if (val) form.setFieldsValue({ date: null });
                }}
              />
            </Form.Item>
          </div>
        </section>

        <div className="submitWrapper">
          <button type="button" className="exportCancel" onClick={onClose}>
            Cancel
          </button>
          <NormalButton htmlType="submit" loading={loading}>
            Export Data
          </NormalButton>
        </div>
      </Form>
    </Modal>
  );
};

export default ExportModal;
